import React, { useState, useEffect } from 'react';
import { reportsApi } from '../services/api';
import { ArrowTrendingUpIcon, CurrencyDollarIcon, ChartBarIcon, TruckIcon, GiftIcon } from '@heroicons/react/24/outline';

interface ShippingSummary {
  total_shipments: number;
  total_cost: number;
  avg_cost: number;
  total_orders: number;
  // New fields
  shipping_collected: number;
  shipping_spent: number;
  shipping_profit: number;
  // Free shipping stats
  free_shipping_order_count: number;
  free_shipping_order_total: number;
  free_shipping_label_cost: number;
  free_shipping_cost_percent: number;
}

interface CarrierBreakdown {
  carrier: string;
  shipment_count: number;
  total_cost: number;
  avg_cost: number;
}

interface OrderVolumeItem {
  period: string;
  order_count: number;
  total_revenue: number;
  shipped_count: number;
  pickup_count: number;
}

export const Reports: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'shipping' | 'orders'>('overview');
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<'7days' | '30days' | '90days' | 'all'>('30days');

  // Report data
  const [shippingSummary, setShippingSummary] = useState<ShippingSummary | null>(null);
  const [carrierBreakdown, setCarrierBreakdown] = useState<CarrierBreakdown[]>([]);
  const [orderVolume, setOrderVolume] = useState<OrderVolumeItem[]>([]);

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const endDate = new Date().toISOString().split('T')[0];
      let startDate: string;
      
      if (dateRange === 'all') {
        startDate = '2020-01-01';
      } else {
        const daysAgo = dateRange === '7days' ? 7 : dateRange === '30days' ? 30 : 90;
        const start = new Date();
        start.setDate(start.getDate() - daysAgo);
        startDate = start.toISOString().split('T')[0];
      }

      const [summaryData, breakdownData, volumeData] = await Promise.all([
        reportsApi.shippingSummary({ start_date: startDate, end_date: endDate }),
        reportsApi.carrierBreakdown({ start_date: startDate, end_date: endDate }),
        reportsApi.orderVolume({ start_date: startDate, end_date: endDate, group_by: dateRange === '7days' ? 'day' : 'day' }),
      ]);

      setShippingSummary(summaryData);
      setCarrierBreakdown(breakdownData.breakdown || []);
      setOrderVolume(volumeData.volume || []);
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getCarrierColor = (carrier: string) => {
    switch (carrier.toLowerCase()) {
      case 'ups': return 'bg-amber-100 text-amber-700';
      case 'usps': return 'bg-blue-100 text-blue-700';
      case 'fedex': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Reports</h1>
          <p className="text-slate-600">
            Analytics and insights for your shipping operations
          </p>
        </div>

        {/* Date Range & Tab Selector */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-subtle mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between px-6 py-4 gap-3">
            {/* Tabs */}
            <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:space-x-1 bg-slate-100 rounded-lg p-1 mb-2 lg:mb-0">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'overview'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('shipping')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'shipping'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Shipping
              </button>
              <button
                onClick={() => setActiveTab('orders')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'orders'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Orders
              </button>
            </div>

            {/* Date Range */}
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as any)}
              className="px-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
            >
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
              <option value="all">All Time</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl border border-slate-200 shadow-subtle p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue"></div>
            <p className="text-slate-600 mt-4">Loading reports...</p>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                  {/* Shipping Profit Card */}
                  <div className="bg-white rounded-xl border border-slate-200 shadow-subtle p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-slate-500">Shipping Profit</span>
                      <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        (shippingSummary?.shipping_profit || 0) >= 0 ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        <ArrowTrendingUpIcon className={`w-5 h-5 ${
                          (shippingSummary?.shipping_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                        }`} />
                      </span>
                    </div>
                    <p className={`text-3xl font-bold ${
                      (shippingSummary?.shipping_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(shippingSummary?.shipping_profit || 0)}
                    </p>
                    <div className="mt-2 text-xs text-slate-500">
                      <span className="text-green-600">{formatCurrency(shippingSummary?.shipping_collected || 0)}</span>
                      <span> collected - </span>
                      <span className="text-red-600">{formatCurrency(shippingSummary?.shipping_spent || 0)}</span>
                      <span> spent</span>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-slate-200 shadow-subtle p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-slate-500">Total Shipping Cost</span>
                      <span className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <CurrencyDollarIcon className="w-5 h-5 text-green-600" />
                      </span>
                    </div>
                    <p className="text-3xl font-bold text-slate-900">
                      {formatCurrency(shippingSummary?.total_cost || 0)}
                    </p>
                  </div>

                  <div className="bg-white rounded-xl border border-slate-200 shadow-subtle p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-slate-500">Avg Cost per Shipment</span>
                      <span className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <ChartBarIcon className="w-5 h-5 text-purple-600" />
                      </span>
                    </div>
                    <p className="text-3xl font-bold text-slate-900">
                      {formatCurrency(shippingSummary?.avg_cost || 0)}
                    </p>
                  </div>

                  <div className="bg-white rounded-xl border border-slate-200 shadow-subtle p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-slate-500">Orders Shipped</span>
                      <span className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <TruckIcon className="w-5 h-5 text-orange-600" />
                      </span>
                    </div>
                    <p className="text-3xl font-bold text-slate-900">
                      {shippingSummary?.total_orders || 0}
                    </p>
                  </div>

                  {/* Free Shipping Cost % Card */}
                  <div className="bg-white rounded-xl border border-slate-200 shadow-subtle p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-slate-500">Free Shipping Cost %</span>
                      <span className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                        <GiftIcon className="w-5 h-5 text-pink-600" />
                      </span>
                    </div>
                    <p className="text-3xl font-bold text-slate-900">
                      {(shippingSummary?.free_shipping_cost_percent || 0).toFixed(1)}%
                    </p>
                    <div className="mt-2 text-xs text-slate-500">
                      <span>{formatCurrency(shippingSummary?.free_shipping_label_cost || 0)}</span>
                      <span> spent on </span>
                      <span>{shippingSummary?.free_shipping_order_count || 0}</span>
                      <span> free shipping orders</span>
                    </div>
                  </div>
                </div>

                {/* Carrier Breakdown */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
                  <div className="px-6 py-4 border-b border-slate-200">
                    <h2 className="text-lg font-semibold text-slate-900">Carrier Breakdown</h2>
                  </div>
                  <div className="p-6">
                    {carrierBreakdown.length === 0 ? (
                      <p className="text-slate-500 text-center py-8">No shipping data available</p>
                    ) : (
                      <div className="space-y-4">
                        {carrierBreakdown.map((carrier, index) => (
                          <div key={index} className="flex items-center">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getCarrierColor(carrier.carrier)} min-w-[100px] justify-center`}>
                              {carrier.carrier.toUpperCase()}
                            </span>
                            <div className="flex-1 mx-4">
                              <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-brand-blue rounded-full"
                                  style={{
                                    width: `${Math.min(100, (carrier.shipment_count / Math.max(...carrierBreakdown.map(c => c.shipment_count))) * 100)}%`
                                  }}
                                />
                              </div>
                            </div>
                            <div className="text-right min-w-[150px]">
                              <p className="text-sm font-medium text-slate-900">{carrier.shipment_count} shipments</p>
                              <p className="text-xs text-slate-500">{formatCurrency(carrier.total_cost)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Shipping Tab */}
            {activeTab === 'shipping' && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
                  <div className="px-6 py-4 border-b border-slate-200">
                    <h2 className="text-lg font-semibold text-slate-900">Shipping Details by Carrier</h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            Carrier
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            Shipments
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            Total Cost
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            Avg Cost
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            % of Total
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {carrierBreakdown.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                              No shipping data available
                            </td>
                          </tr>
                        ) : (
                          carrierBreakdown.map((carrier, index) => {
                            const totalShipments = carrierBreakdown.reduce((sum, c) => sum + c.shipment_count, 0);
                            const percentage = totalShipments > 0 ? (carrier.shipment_count / totalShipments * 100).toFixed(1) : '0';
                            
                            return (
                              <tr key={index}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getCarrierColor(carrier.carrier)}`}>
                                    {carrier.carrier.toUpperCase()}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                                  {carrier.shipment_count}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                                  {formatCurrency(carrier.total_cost)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                                  {formatCurrency(carrier.avg_cost)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                                  {percentage}%
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl border border-slate-200 shadow-subtle">
                  <div className="px-6 py-4 border-b border-slate-200">
                    <h2 className="text-lg font-semibold text-slate-900">Order Volume Over Time</h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            Orders
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            Revenue
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            Shipped
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                            Pickup
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {orderVolume.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                              No order data available
                            </td>
                          </tr>
                        ) : (
                          orderVolume.slice(0, 30).map((item, index) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                                {item.period}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                                {item.order_count}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                                {formatCurrency(item.total_revenue)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                                {item.shipped_count}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                                {item.pickup_count}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Info Panel */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">About Reports</h3>
          <p className="text-sm text-blue-800">
            Reports are updated in real-time based on your shipping activity. Use the date range selector to view different time periods. 
            More detailed analytics including charts and export functionality coming soon.
          </p>
        </div>
      </div>
    </div>
  );
};
